// skipcq: JS-W1028, JS-0128
import React from 'react';
import {
  CardContent,
  CardActions,
  Typography,
  CircularProgress,
  Box,
  Button,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';
import useSystemStats from '../../hooks/useSystemStats';
import useCheckinsTrend from '../../hooks/useCheckinsTrend';
import DashboardSurfaceCard from './DashboardSurfaceCard';
import Sparkline from '../widgets/Sparkline';

/**
 * Displays total check-ins from the latest system stats document.
 */
function CheckinsCard() {
  const { systemStats, loading, error } = useSystemStats();
  const { trendData, loading: trendLoading } = useCheckinsTrend();

  const totalCheckins = systemStats?.metrics?.totalCheckinsCount || 0;

  // Build values for sparkline
  const checkinsValues = trendData.map((item) => item.checkinsCount);

  // Calculate change metrics for a values series
  function calculateChange(values) {
    const firstValue = values[0] || 0;
    const lastValue = values[values.length - 1] || 0;
    const countChange = lastValue - firstValue;
    const percentChange = firstValue > 0 ? (countChange / firstValue) * 100 : 0;
    return { countChange, percentChange };
  }

  // Pick trend icon based on change
  function getTrendIcon(change) {
    if (change > 0) return TrendingUp;
    if (change < 0) return TrendingDown;
    return TrendingFlat;
  }

  // Pick trend color based on change
  function getTrendColor(change) {
    if (change > 0) return 'success.main';
    if (change < 0) return 'error.main';
    return 'text.secondary';
  }

  const checkinsChange = calculateChange(checkinsValues);
  const CheckinsTrendIcon = getTrendIcon(checkinsChange.countChange);

  return (
    <DashboardSurfaceCard>
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          Logged activity
        </Typography>
        <Typography variant="body2" paragraph sx={{
          color: "text.secondary"
        }}>
          Total activity entries logged across the platform
        </Typography>

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 2
            }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Typography variant="body2" color="error">
            Error loading stats
          </Typography>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%"
              }}>
              <Typography
                variant="h4"
                component="div"
                color="primary"
                sx={{ mr: 2 }}
              >
                {totalCheckins.toLocaleString()}
              </Typography>
              {!trendLoading && checkinsValues.length > 0 && (
                <>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Sparkline
                      data={checkinsValues}
                      width="100%"
                      height={24}
                      color="primary.main"
                      strokeWidth={2.5}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      ml: 1
                    }}>
                    <CheckinsTrendIcon
                      sx={{
                        color: getTrendColor(checkinsChange.countChange),
                        fontSize: 16,
                        mr: 0.5,
                      }}
                    />
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: getTrendColor(checkinsChange.countChange),
                          fontWeight: 'medium',
                          lineHeight: 1,
                        }}
                      >
                        {checkinsChange.countChange > 0 ? '+' : ''}
                        {checkinsChange.countChange.toLocaleString()}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: getTrendColor(checkinsChange.countChange),
                          display: 'block',
                          lineHeight: 1,
                        }}
                      >
                        ({checkinsChange.percentChange > 0 ? '+' : ''}
                        {checkinsChange.percentChange.toFixed(1)}%)
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              Total entries logged
            </Typography>
          </>
        )}
      </CardContent>
      <CardActions>
        <Button variant="outlined" component={Link} to="/checkins" fullWidth>
          View logged activity
        </Button>
      </CardActions>
    </DashboardSurfaceCard>
  );
}

export default CheckinsCard;
